import { Protocol as ControlProtocol } from '@studio/models/control';
import { Protocol as VideoProtocol } from '@studio/models/video';
import type { Discovery } from './types.js';
import { Filter } from './types.js';
import { Lang } from '#~/video/index.js';
import { Vendor, getCurrentVendor } from '@studio/design-system/themes/vendor.js';

const vendorPrefixMap: { [key in Vendor]: string } = {
  [Vendor.IRIS]: 'Iris',
  [Vendor.PTZOPTICS]: 'Hive'
};
const vendor = getCurrentVendor() ?? Vendor.IRIS;
const vendorVariant = vendorPrefixMap[vendor];

/** Device filter definitions. */
export const Filters: Record<
  Filter,
  Readonly<{
    /** Filter label. */
    label: string;

    /** Matching function. */
    matches: (µ: Discovery) => boolean;
  }>
> = Object.freeze({
  [Filter.All]: {
    label: 'All connections',
    matches(): boolean {
      return true;
    }
  },

  [Filter.NDI]: {
    label: Lang.protocol[VideoProtocol.NDI],
    matches(µ: Discovery): boolean {
      return 'video' in µ ? µ.video.protocol === VideoProtocol.NDI : false;
    }
  },

  [Filter.ONVIF]: {
    label: 'RTSP (ONVIF)',
    matches(µ: Discovery): boolean {
      return 'control' in µ ? µ.control.protocol === ControlProtocol.ONVIF : false;
    }
  },

  [Filter.IRIS]: {
    label: `${vendorVariant} Enabled`,
    matches(µ: Discovery): boolean {
      return 'video' in µ && µ.irisEnabled === true;
    }
  },

  // Plain RTSP — matches video discoveries using the RTSP, RTSPT, or RTSPLT
  // transport protocols. Excludes ONVIF (a control protocol, not listed here)
  // and other video protocols such as NDI and Iris. Per investigate-before-adding:
  // mirrors the frozen-record convention of the sibling filter entries above.
  [Filter.RTSP]: {
    label: 'RTSP',
    matches(µ: Discovery): boolean {
      if (!('video' in µ)) return false;
      const { protocol } = µ.video;
      return (
        protocol === VideoProtocol.RTSP ||
        protocol === VideoProtocol.RTSPT ||
        protocol === VideoProtocol.RTSPLT
      );
    }
  }
});
